import { useState, useCallback, useEffect, useRef } from 'react';

import { ForwardedInputRef } from 'inpock-ui-react';

interface FormEventHandler {
  (input_value: string | string[] | number | boolean | object, name?: string): void;
}

export function useForm<T extends object>(initialForm: T) {
  const [form, setForm] = useState<T>(initialForm);
  const [validState, setValidState] = useState({} as Partial<Record<keyof T, boolean>>);
  const inputRef = useRef({} as Partial<Record<keyof T, ForwardedInputRef | null>>);

  useEffect(() => {
    const initalValidateState: Partial<Record<keyof T, boolean>> = {};
    for (const key of Object.keys(form)) {
      initalValidateState[key as keyof T] = false;
      inputRef.current[key as keyof T] = null;
    }
    setValidState(initalValidateState);
  }, []);

  const onInput: FormEventHandler = useCallback((input_value, name) => {
    if (!name) {
      console.error(`[useForm]: Set 'name' prop to form element`);
      return;
    }
    setForm((form) => ({
      ...form,
      [name]: input_value,
    }));
  }, []);

  const onComplete: FormEventHandler = useCallback((input_value, name) => {
    if (!name) {
      console.error(`[useForm]: Set 'name' prop to form element`);
      return;
    }
    setValidState((validState) => ({
      ...validState,
      [name]: true,
    }));
  }, []);

  const onInvalid: FormEventHandler = useCallback((input_value, name) => {
    if (!name) {
      console.error(`[useForm]: Set 'name' prop to form element`);
      return;
    }
    setValidState((validState) => ({
      ...validState,
      [name]: false,
    }));
  }, []);

  const register = (name: string) => {
    return {
      name,
      onInput,
      onComplete,
      onInvalid,
    };
  };

  return { form, validState, setValidState, onInput, onComplete, onInvalid, inputRef, register };
}
